<template>
  <div class="d-flex flex-column h-100">
    <div class="container">
      <h3 class="page-title">Profile Information</h3>
      <div class="pa-2">
        <div class="d-flex ga-2 mb-3">
          <div class="d-flex flex-column ga-2 align-center" style="width: 120px;">
            <v-hover v-slot="{ isHovering, props: hoverProps }">
              <v-avatar v-bind="hoverProps" size="96" color="grey-lighten-2" class="cursor-pointer" @click="openFilePicker">
                <!-- Avatar display logic: if there's a picture URL, show it -->
                <div v-if="picture?.url && !pictureLoading" class="pic-container">
                  <img :src="picture.url" alt="Profile Picture" class="pic" />
                </div>
                <span v-else-if="!pictureLoading" class="text-h4 text-no-select">{{ userInitials }}</span>
                <v-progress-circular v-if="pictureLoading" indeterminate color="primary" size="40" />
                <v-fade-transition>
                  <div
                    v-if="isHovering && !pictureLoading"
                    class="position-absolute w-100 h-100 d-flex align-center justify-center"
                    style="inset: 0; background: var(--surface-elevated); opacity: 0.5; pointer-events: none;"
                  >
                    <span class="text-white text-high-emphasis" style="font-size: 12px;">Drag or Upload</span>
                  </div>
                </v-fade-transition>
                <input
                  type="file"
                  ref="fileInput"
                  accept="image/*"
                  style="display: none;"
                  @change="handleFileUpload"
                  :disabled="pictureLoading"
                />
              </v-avatar>
            </v-hover>
            <v-btn v-if="picture?.url" variant="outlined" @click="removePicture" :disabled="pictureLoading">Remove Photo</v-btn>
          </div>
          <v-col @keydown.enter="saveProfile">
            <v-form ref="form">
              <div class="d-flex ga-3 mb-2">
                <v-text-field
                  id="first-name"
                  v-model="firstname"
                  label="First name"
                  variant="outlined"
                  density="compact"
                  prepend-inner-icon="mdi-account"
                  :rules="[rules.required]"
                />
                <v-text-field
                  id="last-name"
                  v-model="lastname"
                  label="Last name"
                  variant="outlined"
                  density="compact"
                  prepend-inner-icon="mdi-account"
                  :rules="[rules.required]"
                />
              </div>
              <v-text-field
                label="Email Address"
                :model-value="stateUser?.email ?? ''"
                type="email"
                variant="outlined"
                density="compact"
                prepend-inner-icon="mdi-email"
                disabled
              />
              <v-text-field
                id="phone-number-input"
                v-model="phoneNumber"
                label="Phone"
                variant="outlined"
                density="compact"
                prepend-inner-icon="mdi-phone"
                :rules="[rules.phoneNumber]"
                class="mb-2"
              />
              <v-text-field
                id="company"
                v-model="company"
                label="Company"
                variant="outlined"
                density="compact"
                prepend-inner-icon="mdi-domain"
              />
            </v-form>
          </v-col>
        </div>

        <div class="d-flex align-item-end justify-end px-3">
          <v-btn @click="saveProfile" variant="outlined" rounded="0" style="height: 40px !important" :loading="loading">
            <v-icon start>mdi-content-save</v-icon>
            <span>Save Changes</span>
          </v-btn>
        </div>
      </div>
    </div>

    <div class="d-flex justify-end mt-auto px-5">
      <v-alert v-if="deletionRequestedAt" type="info" variant="tonal" density="compact" data-cy="account-deletion-requested">
        Deletion requested on {{ deletionRequestedOn }}. Our team will delete your account within one month
        and email you when it is done.
      </v-alert>
      <v-btn
        v-else
        variant="outlined"
        color="error"
        rounded="0"
        prepend-icon="mdi-delete"
        :disabled="loadingDeletionRequest"
        data-cy="account-delete-btn"
        @click="deletionDialog = true"
      >
        Delete my account
      </v-btn>
    </div>

    <account-deletion-dialog v-model="deletionDialog" @requested="deletionRequestedAt = $event" />
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import Storage from '@/storage'
import _ from 'lodash'
import format from 'date-fns/format'
import cognitoAuth from '@/cognito'
import client from '@/graphql/client'
import AccountDeletionRequest from '@/graphql/queries/AccountDeletionRequest'
import AccountDeletionDialog from '@/components/AccountDeletionDialog.vue'

const store = useStore()
const stateUser = computed(() => store.state.user.user)

const storage = new Storage()

const fileInput = ref(null)
const id = ref('')
const firstname = ref('')
const lastname = ref('')
const phoneNumber = ref('')
const company = ref('')
const picture = ref({})
const currentUser = ref(null)
const form = ref(null)
const loading = ref(false)
const pictureLoading = ref(false)
const deletionRequestedAt = ref(null)
const loadingDeletionRequest = ref(true)
const deletionDialog = ref(false)

const deletionRequestedOn = computed(() => {
  return deletionRequestedAt.value ? format(new Date(deletionRequestedAt.value), 'MMMM d, yyyy') : ''
})

const userInitials = computed(() => {
  const first = firstname.value?.charAt(0)?.toUpperCase() || ''
  const last = lastname.value?.charAt(0)?.toUpperCase() || ''
  return first + last
})

const rules = {
  required: (value) => !!value || 'Required.',
  phoneNumber: (value) => {
    const pattern = /^\+[1-9]\d{1,14}$/
    return !value ||
      pattern.test(value) ||
      'Invalid phone number. Must start with a + sign followed by country code and numbers without spaces or punctuation'
  }
}

const saveProfile = async () => {
  try {
    const { valid } = await form.value.validate()

    if (!valid) {
      return
    }

    loading.value = true
    await cognitoAuth.updateAttributes(
      firstname.value,
      lastname.value,
      phoneNumber.value,
      company.value
    )
    await updateUser()

    store.commit('snackbar/showMessage', { content: 'Profile updated successfully' }, { root: true })
  } catch (error) {
    store.commit('snackbar/showMessage', {
      content: 'Error occurred while updating the profile',
      color: 'red'
    }, { root: true })
  } finally {
    loading.value = false
  }
}

const updatePreSignedURL = (user) => {
  if (!user) return

  currentUser.value = _.cloneDeep(user)
  id.value = currentUser.value.id
  if (currentUser.value?.picture && currentUser.value?.picture?.file && currentUser.value?.picture?.file?.key) {
    const cloudFrontUrl = storage.getAvatarCloudFrontUrl(currentUser.value.picture.file.key)
    if (cloudFrontUrl) {
      currentUser.value.picture.url = cloudFrontUrl
      picture.value = currentUser.value.picture
    } else {
      picture.value = { url: null, file: currentUser.value.picture.file }
    }
  }

  firstname.value = currentUser.value.firstname
  lastname.value = currentUser.value.lastname
  phoneNumber.value = currentUser.value.phoneNumber
  company.value = currentUser.value.company
}

const openFilePicker = () => {
  fileInput.value?.click()
}

const handleFileUpload = async (event) => {
  const file = event.target.files[0]
  if (!file) return

  pictureLoading.value = true
  try {
    const mimeType = file.type || ''
    let fileExtension = ''
    if (mimeType.includes('/')) {
      fileExtension = mimeType.split('/')[1] || ''
    }
    fileExtension = (fileExtension || '').toLowerCase().replace(/[^a-z0-9]+/g, '')
    if (!fileExtension) {
      fileExtension = 'bin'
    }
    const sanitizedFileName = `${Date.now()}.${fileExtension}`
    const response = await storage.uploadFile(`profile-pictures/${id.value}/${sanitizedFileName}`, file)
    const { bucket, key } = response

    const cloudFrontUrl = storage.getAvatarCloudFrontUrl(key)

    if (picture.value?.file?.key) {
      await removePictureInS3(picture.value.file.key)
    }
    picture.value = {
      name: file.name,
      url: cloudFrontUrl,
      file: { region: Storage.region, bucket, key },
      mimeType: file.type
    }

    await updateUser()
    store.commit('snackbar/showMessage', { content: 'Profile picture uploaded successfully' }, { root: true })
  } catch (e) {
    store.commit('snackbar/showMessage', {
      content: 'Failed to upload avatar',
      color: 'red'
    }, { root: true })
  } finally {
    pictureLoading.value = false
    event.target.value = null
  }
}

const removePicture = async () => {
  pictureLoading.value = true
  try {
    if (picture.value?.file?.key) {
      await removePictureInS3(picture.value.file.key)
    }
    const variables = {
      ...currentUser.value,
      picture: { name: null, url: null, file: null, mimeType: null }
    }
    await store.dispatch('user/updateUser', variables)
    picture.value = { name: null, url: null, file: null, mimeType: null }
    store.commit('snackbar/showMessage', { content: 'Profile picture removed successfully' }, { root: true })
  } catch (e) {
    store.commit('snackbar/showMessage', {
      content: 'Error occurred while removing the avatar',
      color: 'red'
    }, { root: true })
  } finally {
    pictureLoading.value = false
  }
}

const removePictureInS3 = async (key) => {
  await storage.deleteFile(key)
}

const updateUser = async () => {
  const variables = {
    ...currentUser.value,
    firstname: firstname.value,
    lastname: lastname.value,
    phoneNumber: phoneNumber.value,
    company: company.value,
    picture: picture.value,
    increaseLoginCounter: false
  }
  await store.dispatch('user/updateUser', variables)
}

const loadDeletionRequest = async () => {
  try {
    const res = await client.query({ operationName: 'accountDeletionRequest', query: AccountDeletionRequest })
    deletionRequestedAt.value = res.accountDeletionRequest?.requestedAt || null
  } catch (error) {
    console.error('Could not load the account deletion request', error)
  } finally {
    loadingDeletionRequest.value = false
  }
}

watch(stateUser, (updatedUser) => {
  if (updatedUser) {
    updatePreSignedURL(updatedUser)
  }
})

onMounted(() => {
  updatePreSignedURL(stateUser.value)
  loadDeletionRequest()
})
</script>

<style scoped>
.v-btn:not(.v-btn--icon) {
  border-radius: 4px !important;
}

.page-title {
  margin-bottom: 32px;
  font-size: 20px;
  font-weight: 700;
  color: var(--dark-4, #363A40);
}

.pic-container img {
  width: 96px;
  height: 96px;
  object-fit: cover;
}
</style>
